/**
 * HeaderSearch — Iter258.9
 *
 * Stripe-pattern inline search bar that lives dead-center in the public
 * Header. Always visible. Click → focus → start typing → live results
 * dropdown directly under the bar (no modal flash).
 *
 * Coexists with the PublicCmdKPalette: pressing ⌘K / Ctrl+K (or "/") from
 * anywhere on the page still opens the full-screen palette — that path
 * is preferred for power users + the paste-an-address flow because the
 * modal has more vertical room for results.
 *
 * Detects raw crypto addresses (ETH/BTC/TRON/Solana) and offers a
 * synthetic "Scan this wallet" CTA at the top of the dropdown — same
 * UX as the modal palette.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Loader2, Search, ScanLine,
  Wallet, ShieldCheck, Compass, BookOpen, Newspaper,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter269.1 — self-tuning relevance. Same fire-and-forget pattern as
// PublicCmdKPalette so clicks from the inline header bar feed the same
// click-stats collection.
function trackCmdkClick({ q, to, kind, label }) {
  try {
    axios.post(`${API}/api/public/cmdk/click`, {
      q: q || "", to, kind, label: label || "",
    }, { timeout: 4000 }).catch(() => {});
  } catch { /* never throw */ }
}

// Static top public pages are now SERVED BY THE BACKEND
// (`/api/public/cmdk/search` includes "pages" results derived from
// /app/backend/public_site_index.py). Keep a tiny client-side fallback
// list so the input still renders something useful before the first
// server response on slow networks.
const FALLBACK_PAGES = [
  { to: "/threat-radar/exposed-wallets", label: "Exposed Wallets Leaderboard" },
  { to: "/threat-radar",                 label: "Threat Radar" },
  { to: "/pricing",                      label: "Pricing" },
  { to: "/audits",                       label: "Audit Marketplace" },
  { to: "/contact",                      label: "Contact" },
];

const KIND_HEADERS = {
  scan:   "SCAN THIS WALLET",
  page:   "PAGES",
  wallet: "EXPOSED WALLETS",
  audit:  "DELIVERED AUDITS",
  doc:    "DOCS",
  news:   "NEWS",
};

// Same regex bank as PublicCmdKPalette. Kept duplicated rather than
// shared so this component stays self-contained (no circular import).
const ADDR_PATTERNS = [
  { name: "ethereum", re: /^0x[0-9a-fA-F]{40}$/ },
  { name: "bitcoin",  re: /^(bc1|tb1)[02-9ac-hj-np-z]{6,87}$/i },
  { name: "bitcoin",  re: /^[13][1-9A-HJ-NP-Za-km-z]{25,40}$/ },
  { name: "tron",     re: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ },
  { name: "solana",   re: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/ },
];

function detectAddress(raw) {
  const v = (raw || "").trim();
  if (v.length < 25 || v.length > 95) return null;
  for (const { name, re } of ADDR_PATTERNS) {
    if (re.test(v)) return { chain: name, address: v };
  }
  return null;
}


function fuzzyScore(label, q) {
  if (!q) return 0;
  const a = label.toLowerCase();
  const needle = q.toLowerCase();
  if (a.startsWith(needle))     return 3;
  if (a.includes(` ${needle}`)) return 2;
  if (a.includes(needle))       return 1;
  let i = 0;
  for (const c of a) {
    if (c === needle[i]) i++;
    if (i === needle.length) return 0.5;
  }
  return 0;
}


const PLACEHOLDER_PHRASES = [
  "Search wallets, audits, pages…",
  "Try Bitfinex",
  "Paste a crypto address to scan it",
  "Press \u2318K to focus",
];

// Typewriter-cycler hook. Returns the currently visible substring of
// the active phrase + advances forward (typing) and backward (deleting)
// at human-like cadences. Pauses entirely when `paused` flips true
// (e.g. user focuses the input) and freezes the visible text — so the
// placeholder doesn't keep dancing while they read.
function useTypewriterPlaceholder(paused) {
  const [phraseIdx, setPhraseIdx] = React.useState(0);
  const [text, setText] = React.useState("");
  const [phase, setPhase] = React.useState("typing");   // typing | hold | deleting | pause

  React.useEffect(() => {
    if (paused) return;
    const phrase = PLACEHOLDER_PHRASES[phraseIdx];
    let delay = 60;
    if (phase === "typing") {
      if (text.length < phrase.length) {
        delay = 55 + Math.random() * 35;      // jitter so it feels human
        const t = setTimeout(() => setText(phrase.slice(0, text.length + 1)), delay);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setPhase("hold"), 1600);
        return () => clearTimeout(t);
      }
    } else if (phase === "hold") {
      const t = setTimeout(() => setPhase("deleting"), 200);
      return () => clearTimeout(t);
    } else if (phase === "deleting") {
      if (text.length > 0) {
        delay = 22;
        const t = setTimeout(() => setText(phrase.slice(0, text.length - 1)), delay);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setPhase("pause"), 240);
        return () => clearTimeout(t);
      }
    } else if (phase === "pause") {
      const t = setTimeout(() => {
        setPhraseIdx((i) => (i + 1) % PLACEHOLDER_PHRASES.length);
        setPhase("typing");
      }, 220);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [paused, text, phase, phraseIdx]);

  return text;
}


export default function HeaderSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [backend, setBackend] = useState({
    pages: [], wallets: [], audits: [], docs: [], news: [],
  });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const isMac = typeof navigator !== "undefined" &&
                /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");

  // Iter258.10 — typewriter placeholder. Cycles through 4 phrases so
  // first-time visitors discover the address-paste superpower + the
  // ⌘K shortcut without us eating any extra screen space.
  // Paused when the user focuses the input or has typed anything,
  // so it never dances while they're reading or editing.
  const typewriter = useTypewriterPlaceholder(open || q.length > 0);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  // Pages come from the backend now (full site index lives in
  // /app/backend/public_site_index.py). We only render a fallback list
  // locally when there's no query yet OR before the first response.
  const pageHits = useMemo(() => {
    const term = q.trim();
    if (!term) return [];
    if ((backend.pages || []).length > 0) return backend.pages;
    // Offline / pre-first-response fallback.
    return FALLBACK_PAGES
      .map((p) => ({ ...p, _score: fuzzyScore(p.label, term) }))
      .filter((p) => p._score > 0)
      .sort((a, b) => b._score - a._score);
  }, [q, backend.pages]);

  // Debounced backend search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setBackend({ pages: [], wallets: [], audits: [], docs: [], news: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/public/cmdk/search`, {
          params: { q: term },
        });
        setBackend(r.data?.results_by_kind || {
          pages: [], wallets: [], audits: [], docs: [], news: [],
        });
      } catch {
        setBackend({ pages: [], wallets: [], audits: [], docs: [], news: [] });
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [open, q]);

  const visibleItems = useMemo(() => {
    const items = [];
    const detected = detectAddress(q);
    if (detected) {
      const short = `${detected.address.slice(0, 10)}…${detected.address.slice(-6)}`;
      items.push({
        to: `/scan/bulk?prefill=${encodeURIComponent(detected.address)}`,
        label: `Scan ${short}`,
        sublabel: `Free HNDL exposure check on this ${detected.chain.toUpperCase()} address`,
        icon: ScanLine, testid: `scan-${detected.chain}`, _kind: "scan",
      });
    }
    items.push(...pageHits.map((p) => ({
      to: p.to, label: p.label,
      sublabel: p.sublabel || "",
      icon: Compass,
      _kind: "page",
    })));
    items.push(...(backend.wallets || []).map((w, i) => ({
      to: w.to, label: w.label, sublabel: w.sublabel,
      icon: Wallet, testid: `wallet-${i}`, _kind: "wallet",
    })));
    items.push(...(backend.audits || []).map((a, i) => ({
      to: a.to, label: a.label, sublabel: a.sublabel,
      icon: ShieldCheck, testid: `audit-${i}`, _kind: "audit",
    })));
    items.push(...(backend.docs || []).map((d, i) => ({
      to: d.to, label: d.label, sublabel: d.sublabel,
      icon: BookOpen, testid: `doc-${i}`, _kind: "doc",
    })));
    items.push(...(backend.news || []).map((n, i) => ({
      to: n.to, label: n.label, sublabel: n.sublabel,
      icon: Newspaper, testid: `news-${i}`,
      external: n.external, _kind: "news",
    })));
    return items.slice(0, 14);     // hard cap — keeps the dropdown bounded
  }, [q, pageHits, backend]);

  // Keyboard nav.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, visibleItems.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const hit = visibleItems[activeIdx];
        if (hit) {
          trackCmdkClick({
            q, to: hit.to, kind: hit._kind, label: hit.label,
          });
          setOpen(false);
          setQ("");
          if (hit.external) {
            window.open(hit.to, "_blank", "noopener,noreferrer");
          } else {
            navigate(hit.to);
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, visibleItems, activeIdx, navigate]);

  return (
    <div ref={containerRef}
         data-testid="header-search-container"
         style={{ position: "relative", flex: "1 1 auto",
                  maxWidth: 480, minWidth: 220 }}>
      <div
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "7px 12px",
          background: "rgba(255,255,255,0.04)",
          border: `1px solid ${open ? "var(--qt-cyan)" : "var(--qt-border)"}`,
          borderRadius: 999,
          cursor: "text",
          transition: "border-color 160ms",
        }}>
        <Search size={13} style={{ color: "var(--qt-text-3)" }} />
        <div style={{
              flex: 1, position: "relative",
              display: "flex", alignItems: "center",
              minWidth: 0,
            }}>
          {/* Animated typewriter ghost text — sits behind the real input
              when q is empty. We do it as a sibling absolute div instead
              of mutating `placeholder` so the caret render is identical
              every frame (placeholder swaps cause iOS Safari to repaint
              the whole input). */}
          {q.length === 0 && !open && (
            <span
              aria-hidden
              data-testid="header-search-typewriter"
              style={{
                position: "absolute", inset: 0,
                display: "flex", alignItems: "center",
                color: "var(--qt-cyan)",
                opacity: 0.85,
                fontSize: 13,
                pointerEvents: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typewriter}
              <span
                style={{
                  display: "inline-block",
                  width: 1.5, height: "1em",
                  background: "var(--qt-cyan)",
                  marginLeft: 1.5,
                  opacity: 0.8,
                  animation: "qt-caret-blink 1s steps(2, start) infinite",
                  transform: "translateY(1px)",
                }}
              />
            </span>
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder=""
            data-testid="header-search-input"
            style={{
              flex: 1, background: "transparent",
              border: 0, outline: 0,
              color: "#fff", fontSize: 13,
              width: "100%",
            }}
          />
        </div>
        {loading && (
          <Loader2 size={12} className="animate-spin"
                   style={{ color: "var(--qt-text-3)" }} />
        )}
        <span
          className="qt-mono"
          style={{
            fontSize: 9, padding: "1px 5px", borderRadius: 3,
            color: "var(--qt-text-3)",
            border: "1px solid var(--qt-border)",
            letterSpacing: "0.12em",
          }}
        >
          {isMac ? "⌘K" : "CTRL K"}
        </span>
      </div>

      {open && q.trim() && (
        <div
          data-testid="header-search-dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0, right: 0,
            background: "#04091A",
            border: "1px solid rgba(0,229,255,0.32)",
            borderRadius: 6,
            boxShadow: "0 16px 40px rgba(0,0,0,0.7)",
            maxHeight: "65vh",
            overflowY: "auto",
            zIndex: 60,
            padding: "6px 0",
          }}>
          {visibleItems.length === 0 && !loading && (
            <div data-testid="header-search-empty"
                 style={{
                   padding: "16px 14px", color: "var(--qt-text-3)",
                   fontSize: 13, textAlign: "center",
                 }}>
              No pages, wallets, or audits match &ldquo;{q}&rdquo;.
            </div>
          )}
          {visibleItems.map((item, idx) => {
            const Icon = item.icon;
            const active = idx === activeIdx;
            const prevKind = idx > 0 ? visibleItems[idx - 1]._kind : null;
            const header = (item._kind !== prevKind) ? KIND_HEADERS[item._kind] : null;
            return (
              <React.Fragment key={`${item._kind}-${item.to}-${idx}`}>
                {header && (
                  <div className="qt-mono"
                       style={{ padding: "8px 14px 4px",
                                color: "var(--qt-text-3)",
                                fontSize: 9, letterSpacing: "0.14em" }}>
                    {header}
                  </div>
                )}
                <button
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => {
                    trackCmdkClick({
                      q, to: item.to, kind: item._kind, label: item.label,
                    });
                    setOpen(false); setQ("");
                    if (item.external) {
                      window.open(item.to, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(item.to);
                    }
                  }}
                  data-testid={`header-search-item-${item.testid || idx}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%",
                    padding: "9px 14px",
                    background: active ? "rgba(0,229,255,0.10)" : "transparent",
                    border: 0,
                    borderLeft: `2px solid ${active ? "var(--qt-cyan)" : "transparent"}`,
                    color: active ? "#fff" : "var(--qt-text-2)",
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}>
                  {Icon && <Icon size={13}
                                 style={{ color: active ? "var(--qt-cyan)" : "var(--qt-text-3)" }} />}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: "block", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{item.label}</span>
                    {item.sublabel && (
                      <span className="qt-mono"
                            style={{ display: "block", fontSize: 9,
                                     color: "var(--qt-text-3)",
                                     letterSpacing: "0.04em",
                                     overflow: "hidden",
                                     textOverflow: "ellipsis",
                                     whiteSpace: "nowrap" }}>
                        {item.sublabel}
                      </span>
                    )}
                  </span>
                  {active && <ArrowRight size={11} />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
